import React, { useState, useContext, useEffect } from "react";
import styled from "styled-components";
import axios from "axios";
import { GlobalState } from "../../GlobalState";
import { editAccess } from "../utils/utils";
import { FaClock } from "react-icons/fa";

const ShowFormToggle = styled.button`
  display: block;
  cursor: pointer;
  margin: 0.8rem auto;
  color: var(--background);
  background-color: var(--primary);
  border: none;
  outline: none;
  font-size: 1rem;
  padding: 0.5rem;
  border-radius: 30px;
`;

const Container = styled.div`
  padding: 0.5rem;
`;

const Content = styled.div`
  margin: 0 auto;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 20px;
  background-color: var(--secondary);
`;

const InputWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 0.6rem 1.1rem;
  margin: 0.5rem auto;
`;

const StyledInput = styled.input`
  background-color: var(--background);
  padding: 0.6rem;
  font-size: 1rem;
  border-radius: 10px;
  width: 100%;
  &:focus {
    border: 2px solid var(--primary);
  }
`;

const ErrMessage = styled.p`
  font-size: 0.7rem;
  color: var(--primary);
`;

const SuccessMessage = styled.p`
  font-size: 0.7rem;
  color: green;
`;

const StyledForm = styled.form`
  width: 100%;
`;

const StyledBtn = styled(ShowFormToggle)`
  width: 100%;
  font-size: 1rem;
`;

const CurrentValue = styled.p`
  font-size: 0.9rem;
  color: var(--primary);
  margin-bottom: 0.5rem;
`;

const DisplayContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  gap: 0.5rem;
`;

const UnitValueDisplay = styled.div`
  background-color: var(--secondary);
  padding: 1rem;
  border-radius: 20px;
  width: 260px;
  text-align: center;
`;

const ValueLabel = styled.p`
  font-size: 0.9rem;
  color: var(--text);
  margin-bottom: 0.3rem;
`;

const ValueText = styled.h2`
  font-size: 1.5rem;
  color: var(--primary);
  font-weight: bold;
  margin-bottom: 0.4rem;
`;

const ModifiedDate = styled.p`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  font-size: 0.8rem;
  color: gray;
  margin-top: 0.3rem;

  .clock-icon {
    color: gray;
    font-size: 0.9rem;
  }
`;

export default function UpdateUnitValue({ loading, userData }) {
  const state = useContext(GlobalState);
  const [showForm, setShowForm] = useState(false);
  const [unitValue, setUnitValue] = useState("");
  const [previousValue, setPreviousValue] = useState("");
  const [err, setErr] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [lastModified, setLastModified] = useState("");

useEffect(() => {
  const fetchUnitRate = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API}/user/unitrate/${userData.org_id}`,
        {
          headers: {
            Authorization: state.token[0],
          },
        }
      );

      const data = res.data;
      console.log("Unit Rate Response:", data);

      const validRate = parseFloat(data?.current_unit_rate);
      setUnitValue(
        !isNaN(validRate) && validRate > 0 ? validRate.toFixed(2) : "7.35"
      );

      setPreviousValue(data.previous_unit_rate ?? "-");

      if (data.updatedAt) {
        const dateObj = new Date(data.updatedAt);

        const formattedDate = dateObj.toLocaleDateString([], {
          year: "numeric",
          month: "short",
          day: "numeric",
        });

        const formattedTime = dateObj.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });

        setLastModified(`${formattedDate}, ${formattedTime}`);
      } else {
        setLastModified("");
      }
    } catch (err) {
      console.error("Error fetching unit rate:", err.message);
      setUnitValue("7.35");
    }
  };

  if (userData?.org_id && state?.token?.[0]) {
    fetchUnitRate();
  }
}, [userData?.org_id, state?.token]);

  const handleFormToggleClick = () => {
    setErr("");
    setSuccessMsg("");
    setShowForm(!showForm);
  };

  const UpdateUnitValueSubmit = async (e) => {
    e.preventDefault();
    if (!unitValue || unitValue.trim() === "") {
      setErr("Unit value cannot be empty.");
      return;
    }

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API}/user/unitrate/${userData.org_id}`,
        {
          newRate: parseFloat(unitValue),
          remarks: "Updated from dashboard",
        },
        {
          headers: { Authorization: state.token[0] },
        }
      );

      if (res.status === 200 || res.status === 201) {
        setSuccessMsg("Unit value updated successfully!");
        setLastModified(new Date().toLocaleString());
        setTimeout(() => handleFormToggleClick(), 1500);
      }
    } catch (err) {
      console.error("Error updating unit rate:", err);
      setErr(
        err.response?.data?.message ||
          "Failed to update unit value. Please try again."
      );
    }
  };

  if (!editAccess(userData)) return null;

  return (
    <>
      {showForm ? (
        <Container>
          <Content>
            <CurrentValue>Current Unit Value: {unitValue}</CurrentValue>

            <StyledForm onSubmit={UpdateUnitValueSubmit}>
              <InputWrapper>
                <StyledInput
                  type="number"
                  name="unit_value"
                  step="0.01"
                  placeholder="Enter New Unit Value"
                  value={unitValue}
                  onChange={(e) => setUnitValue(e.target.value)}
                  required
                />
              </InputWrapper>

              <StyledBtn type="submit">Submit</StyledBtn>
            </StyledForm>

            {err && <ErrMessage>{err}</ErrMessage>}
            {successMsg && <SuccessMessage>{successMsg}</SuccessMessage>}

            <StyledBtn onClick={handleFormToggleClick}>Cancel</StyledBtn>
          </Content>
        </Container>
      ) : (
        <DisplayContainer>
          <UnitValueDisplay>
            <ValueLabel>Current Unit Value</ValueLabel>
            <ValueText>{unitValue}</ValueText>

            {/* {previousValue && (
              <p style={{ fontSize: "0.8rem", color: "gray" }}>
                Previous: {previousValue}
              </p>
            )} */}

            <ShowFormToggle onClick={handleFormToggleClick}>
              Update Unit Value
            </ShowFormToggle>

            {/* {lastModified && (
              <ModifiedDate>
                <FaClock className="clock-icon" />
                <span>{lastModified}</span>
              </ModifiedDate>
            )} */}
          </UnitValueDisplay>
        </DisplayContainer>
      )}
    </>
  );
}
